import type { MenuItemData, ScopeType } from '@/data/menuData'
import { AllowedAction } from '@/data/allowedActions'
import type { UserScopes } from '@magnetar/admin-api-interfaces'
import type { ViewMode } from '@/stores/viewMode'

/**
 * 決定一個側邊欄選單項目對當前使用者是否可見。
 *
 * 解析順序：
 *   1-3. showForRoles / showForScopes —— 這個項目對此 role/scope 是否相關。
 *   3.5  exactPermission —— 嚴格 gate：不吃 sys_admin god-view、也不吃階層繼承，
 *        只認 item.permission 本身。用於 org 內部「角色管理」，後端視其為 provider
 *        主權（matrix X：只有擁有該組織的 provider 能管角色表；sys_admin 與下層 403）。
 *        見 apps/admin-api/src/routes/roles 與
 *        apps/admin-api/__tests__/permissions/scopedRolesMatrix.test.ts。
 *   4.   sys_admin —— 其餘項目的 god-view 放行。
 *   5.   階層 —— 有管理上層組織的權限，下層子單位的功能全部可見。
 *   6.   permission —— 一般 scoped 使用者依項目自身權限判斷。
 */
export const filterMenuItem = (
  item: MenuItemData,
  hasPermission: (module: string, action: AllowedAction) => boolean,
  userRoleType?: string,
  userScopes?: UserScopes,
  mode?: ViewMode,
): boolean => {
  // 1. 檢查 showForRoles（只用於 sys_admin 和 investor）
  const hasRoleMatch = item.showForRoles?.length
    ? userRoleType && item.showForRoles.includes(userRoleType as 'sys_admin' | 'investor')
    : false

  // 2. 檢查 showForScopes（根據用戶關聯判斷）
  const hasScopeMatch =
    item.showForScopes?.length && userScopes
      ? item.showForScopes.some((scope: ScopeType) => {
          if (scope === 'provider') return userScopes.provider_id !== null
          if (scope === 'clinic') return userScopes.clinic_ids.length > 0
          if (scope === 'ad_supplier') return userScopes.supplier_ids.length > 0
          return false
        })
      : false

  // 3. 如果有設定 showForRoles 或 showForScopes，必須至少符合一個
  if (item.showForRoles?.length || item.showForScopes?.length) {
    if (!hasRoleMatch && !hasScopeMatch) {
      return false
    }
  }

  // 4. sys_admin god-view：sys_admin 對所有項目都可見。對 org 角色端點，後端 READ 已放行
  // sys_admin（bypass，read-only oversight），所以放行不會造成「看得到卻 403」。
  if (userRoleType === 'sys_admin') {
    return true
  }

  // 4.5 嚴格主權項目（org 內部角色表的「角色管理」）：後端寫入只認「擁有該組織的 provider
  // 管理權」，READ 也只放行該 provider（與上面已處理的 sys_admin）。所以對非 sys_admin 的
  // 使用者，這裡不吃階層繼承，只認 item.permission 本身——menu 才不會對一般 scoped 使用者
  // 顯示他們點了會 403 的角色管理（這正是舊 step 5 階層過授權的洞）。
  if (item.exactPermission) {
    return item.permission
      ? hasPermission(item.permission.module, item.permission.action)
      : false
  }

  // 5. 階層權限檢查：上層有管理下層的權限，則下層所有功能都可見
  // - sys_admin                                     → step 4 已處理
  // - provider 有 provider_clinic_management        → 可見 clinic / clinic_group 全部功能
  // - provider 有 provider_supplier_management      → 可見 ad / ad_supplier_group 全部功能
  // - clinic_group 有 clinic_group_clinic_management → 可見 clinic 全部功能（集團下診所）
  // - ad_supplier_group 有對應管理權                 → 可見 ad 全部功能
  if (
    mode === 'clinic' &&
    (hasPermission('provider_clinic_management', AllowedAction.R) ||
      hasPermission('clinic_group_clinic_management', AllowedAction.R))
  ) {
    return true
  }
  if (
    mode === 'ad' &&
    (hasPermission('provider_supplier_management', AllowedAction.R) ||
      hasPermission('ad_supplier_group_supplier_management', AllowedAction.R))
  ) {
    return true
  }
  if (mode === 'clinic_group' && hasPermission('provider_clinic_management', AllowedAction.R)) {
    return true
  }
  if (
    mode === 'ad_supplier_group' &&
    hasPermission('provider_supplier_management', AllowedAction.R)
  ) {
    return true
  }

  // 6. 檢查權限（其他角色）
  if (item.permission) {
    return hasPermission(item.permission.module, item.permission.action)
  }

  return true
}
